import { useMutation, useQuery } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CheckIcon } from '@chakra-ui/icons'
import { Box, Button, ButtonGroup, IconButton, Input } from '@chakra-ui/react'
import { Checkbox, FormControl, FormLabel, Textarea } from '@chakra-ui/react'
import { Select, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'

import Card from 'components/Card/Card'
import CustomLoader from 'components/CustomLoader'

import useCustomToast from 'hooks/useCustomToast'
import { useHasPermission } from 'hooks/useHasPermission'
import { useThemeColor } from 'hooks/useThemeColors'

import { updateCompVulnVex } from 'graphQL/Mutation'
import { GetCdxResponses, getVexJustifications } from 'graphQL/Queries'
import { GetCustomFields, getVexStatuses } from 'graphQL/Queries'
import { ComponentVulnLogs } from 'graphQL/Queries'

import { FaTimes } from 'react-icons/fa'

import HistoryDrawer from './HistoryDrawer'
import InfoLabel from './InfoLabel'

const VexStatusComponent = ({ data }) => {
  const { id, fixedVersions, componentVulnCustomFields } = data || ''

  const paramz = useParams()
  const sbomId = paramz.sbomid
  const { showToast } = useCustomToast()

  const HISTORY = useDisclosure()

  const { primaryTextColor, grayBorderColor, headingTextColor } = useThemeColor(
    ['primaryTextColor', 'grayBorderColor', 'headingTextColor']
  )

  const editVulns = useHasPermission({
    parentKey: 'view_sbom',
    childKey: 'edit_vulnerabilities'
  })

  const [statusName, setStatusName] = useState(data?.vexStatus?.name || '')
  const [vexStatusId, setVexStatusId] = useState(data?.vexStatus?.id || '')
  const [vexJustificationId, setVexJustificationId] = useState(
    data?.vexJustification?.id || ''
  )
  const [cdxResponseId, setCdxResponseId] = useState(
    data?.cdxResponse?.id || ''
  )
  const [responseTitle, setResponseTitle] = useState(
    data?.cdxResponse?.name || ''
  )
  const [params, setParams] = useState({})
  const [fixedIn, setFixedIn] = useState(data?.fixedIn || '')
  const [detail, setDetail] = useState(data?.detail || '')
  const [note, setNote] = useState(data?.note || '')
  const [impact, setImpact] = useState(data?.impact || '')
  const [action, setAction] = useState(data?.actionStmt || '')
  const [propagateVex, setPropagateVex] = useState(false)
  const [saveButtonDisable, setSaveButtonDisable] = useState(false)
  const [statusCheckType, setStatusCheckType] = useState('')
  const [detailsCheckType, setDetailsCheckType] = useState('')
  const [riskCheckType, setRiskCheckType] = useState('')
  const [notesCheckType, setNotesCheckType] = useState('')
  const [locallySavedLabel, setLocallySavedLabel] = useState(null)

  const { data: fields, loading: fieldLoading } = useQuery(GetCustomFields)
  const { componentVulnCustomFieldDefinitions } = fields || ''
  const { nodes } = componentVulnCustomFieldDefinitions || ''

  const { data: logs } = useQuery(ComponentVulnLogs, {
    skip: data?.id ? false : true,
    variables: { vexLogableId: data?.id }
  })

  const { componentVulnLogs } = logs || ''

  const { data: allVexStatus } = useQuery(getVexStatuses)
  const { data: allVexJustify } = useQuery(getVexJustifications, {
    skip: statusName !== 'Not Affected'
  })
  const { data: allCdx } = useQuery(GetCdxResponses, {
    skip: statusName === 'Affected' ? false : true
  })

  const fieldOne = nodes?.find((item) => item?.fieldType === 'RANGE')
  const fieldTwo = nodes?.find((item) => item?.fieldType === 'TEXT')

  const [formValues, setFormValues] = useState(null)

  const handleChange = (id, value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [id]: value
    }))
  }

  const handleStatusChange = (e) => {
    const { value } = e.target
    const status = e.target.options[e.target.selectedIndex].text
    status === 'Not Affected' ? setPropagateVex(true) : setPropagateVex(false)
    setStatusName(status)
    setVexStatusId(value)
    generateControl(status)
  }

  const handleResponseChange = (e) => {
    const { value } = e.target
    const title = e.target.options[e.target.selectedIndex].text

    setResponseTitle(title)
    setCdxResponseId(value)
  }

  const handleJustifyChange = (e) => {
    const { value } = e.target
    setVexJustificationId(value)
  }

  const handleFixedInChange = (e) => {
    const { value } = e.target
    setFixedIn(value)
  }

  const [compVexCreate, { loading }] = useMutation(updateCompVulnVex)

  const checkEmpty = (value) => value || undefined

  const isValuePresent =
    nodes?.length > 0 && formValues && formValues[fieldOne?.id] !== ''

  const isInvalid =
    statusName === 'Affected' &&
    componentVulnCustomFields?.length > 0 &&
    isValuePresent === false

  const generateControl = useCallback(
    (status) => {
      isValuePresent ? setRiskCheckType('green') : setRiskCheckType('red')
      switch (status) {
        case 'In Triage':
          setParams({
            sbomId: checkEmpty(sbomId),
            compVulnId: checkEmpty(id),
            vexStatusId: checkEmpty(vexStatusId),
            propagateVex: checkEmpty(propagateVex),
            detail: checkEmpty(detail),
            note: checkEmpty(note)
          })

          setSaveButtonDisable(false)

          setStatusCheckType('green')
          detail ? setDetailsCheckType('green') : setDetailsCheckType('grey')
          note ? setNotesCheckType('green') : setNotesCheckType('grey')
          break

        case 'Not Affected':
          setParams({
            sbomId: checkEmpty(sbomId),
            compVulnId: checkEmpty(id),
            vexStatusId: checkEmpty(vexStatusId),
            propagateVex: checkEmpty(propagateVex),
            note: checkEmpty(note),
            vexJustificationId: checkEmpty(vexJustificationId),
            impact: checkEmpty(impact)
          })

          setSaveButtonDisable(false)

          setStatusCheckType('green')

          if (!vexJustificationId && !impact) {
            setDetailsCheckType('red')
          } else {
            setDetailsCheckType('green')
          }

          note ? setNotesCheckType('green') : setNotesCheckType('grey')

          break

        case 'Affected':
          setParams({
            sbomId: checkEmpty(sbomId),
            compVulnId: checkEmpty(id),
            vexStatusId: checkEmpty(vexStatusId),
            propagateVex: checkEmpty(propagateVex),
            note: checkEmpty(note),
            cdxResponseId: checkEmpty(cdxResponseId),
            action: checkEmpty(action),
            fixedIn: checkEmpty(fixedIn)
          })

          setSaveButtonDisable(false)

          setStatusCheckType('green')

          if (!cdxResponseId && !action) {
            setDetailsCheckType('red')
          } else if (responseTitle == 'Update' && !fixedIn) {
            setSaveButtonDisable(true)
          } else if (cdxResponseId && !action) {
            setDetailsCheckType('red')
          } else {
            setDetailsCheckType('green')
          }

          note ? setNotesCheckType('green') : setNotesCheckType('grey')
          break

        case 'Fixed':
          setParams({
            sbomId: checkEmpty(sbomId),
            compVulnId: checkEmpty(id),
            vexStatusId: checkEmpty(vexStatusId),
            propagateVex: checkEmpty(propagateVex),
            note: checkEmpty(note)
          })

          setSaveButtonDisable(false)
          setStatusCheckType('green')
          note ? setNotesCheckType('green') : setNotesCheckType('grey')
          break

        default:
          setParams({
            sbomId: checkEmpty(sbomId),
            compVulnId: checkEmpty(id),
            vexStatusId: checkEmpty(vexStatusId),
            note: checkEmpty(note)
          })

          setStatusCheckType('red')

          if (formValues && formValues[fieldOne?.id] === '') {
            setSaveButtonDisable(true)
          } else {
            setSaveButtonDisable(false)
          }

          if (note) {
            setNotesCheckType('green')
          } else {
            setNotesCheckType(null)
          }

          break
      }
    },
    [
      isValuePresent,
      sbomId,
      id,
      vexStatusId,
      propagateVex,
      detail,
      note,
      vexJustificationId,
      impact,
      cdxResponseId,
      action,
      fixedIn,
      responseTitle,
      formValues,
      fieldOne?.id
    ]
  )

  const generateIcon = (checkIconState) => {
    switch (checkIconState) {
      case 'grey':
        return (
          <IconButton
            size='xs'
            position={'absolute'}
            left={-8}
            top={'49%'}
            colorScheme='gray'
            rounded={'full'}
            icon={<CheckIcon />}
          />
        )
      case 'green':
        return (
          <IconButton
            size='xs'
            position={'absolute'}
            left={-8}
            top={'49%'}
            colorScheme='green'
            rounded={'full'}
            icon={<CheckIcon />}
          />
        )
      case 'red':
        return (
          <IconButton
            size='xs'
            position={'absolute'}
            left={-8}
            top={'49%'}
            colorScheme='red'
            rounded={'full'}
            icon={<FaTimes />}
          />
        )
      default:
        return null
    }
  }

  const getId = (key) => {
    const output = componentVulnCustomFields?.find(
      (item) => item?.componentVulnCustomFieldDefinitionId === key
    )
    return output ? output?.id : undefined
  }

  const result = formValues
    ? Object.entries(formValues)?.map(([key, value]) => ({
        value: value,
        _destroy: value === '' ? true : undefined,
        componentVulnCustomFieldDefinitionId: key,
        id: componentVulnCustomFields?.length > 0 ? getId(key) : undefined
      }))
    : undefined

  const handleSave = () => {
    compVexCreate({
      variables: {
        ...params,
        componentVulnCustomFieldAttributes: result
      }
    }).then(({ data }) => {
      if (data?.componentVexUpdate?.errors?.length === 0) {
        setLocallySavedLabel('Saved')
        setSaveButtonDisable(true)
        showToast({
          description: 'Status updated successfully',
          status: 'success'
        })
      }
    })
  }

  const border = `1px solid ${grayBorderColor}`

  useEffect(() => {
    if (componentVulnCustomFields?.length > 0) {
      const result = componentVulnCustomFields?.reduce((acc, item) => {
        acc[item.componentVulnCustomFieldDefinitionId] = item.value
        return acc
      }, {})
      setFormValues(result)
    } else {
      const data = nodes?.reduce((acc, field) => {
        acc[field.id] = ''
        return acc
      }, {})
      setFormValues(data || null)
    }
  }, [componentVulnCustomFields, nodes])

  useEffect(() => {
    generateControl(statusName)
    setLocallySavedLabel(null)
  }, [
    note,
    detail,
    impact,
    action,
    fixedIn,
    vexStatusId,
    vexJustificationId,
    cdxResponseId,
    propagateVex,
    generateControl,
    statusName
  ])

  if (loading || fieldLoading) return <CustomLoader />

  return (
    <>
      <Stack spacing='24px' w={'100%'}>
        <Box color={primaryTextColor}>
          <SimpleGrid row={5} spacing={4}>
            {/* STATUS */}
            <Card position='relative' p={6} border={border}>
              {generateIcon(statusCheckType)}
              <FormControl isRequired={nodes?.length === 0}>
                <InfoLabel title={'Status'} name={'vexType'} />
                <Select
                  id='vexType'
                  name='vexType'
                  fontSize='sm'
                  value={vexStatusId}
                  aria-label='vex_status'
                  onChange={handleStatusChange}
                  isDisabled={!editVulns}
                >
                  <option value=''>-- Select Status --</option>
                  {allVexStatus ? (
                    allVexStatus.vexStatuses.map((st, idx) => (
                      <option key={idx} value={st.id}>
                        {st.name}
                      </option>
                    ))
                  ) : (
                    <option value={''}>No data found</option>
                  )}
                </Select>
              </FormControl>
            </Card>

            {/* JUSTIFICATION */}
            {statusName === 'Not Affected' && (
              <Card position='relative' p={6} border={border}>
                {generateIcon(detailsCheckType)}
                <FormControl>
                  <InfoLabel
                    title={'Justification'}
                    name={vexJustificationId}
                  />
                  <Select
                    fontSize='sm'
                    id='vexJustificationId'
                    name='vexJustificationId'
                    value={vexJustificationId}
                    aria-label='vex_justification'
                    onChange={handleJustifyChange}
                  >
                    <option value=''>-- Select --</option>
                    {allVexJustify ? (
                      allVexJustify.vexJustifications?.map((justify, idx) => (
                        <option key={idx} value={justify.id}>
                          {justify.name}
                        </option>
                      ))
                    ) : (
                      <option value={''}>No data found</option>
                    )}
                  </Select>
                </FormControl>
                <Text
                  fontSize='sm'
                  fontWeight={'medium'}
                  color={headingTextColor}
                  mt={5}
                >
                  AND / OR
                </Text>
                {/* IMPACT STATEMENT */}
                <FormControl mt={5}>
                  <InfoLabel
                    title={'Impact Statement'}
                    name={'impactStatement'}
                  />
                  <Textarea
                    type='text'
                    name='impactStatement'
                    rows={2}
                    id='impactStatement'
                    placeholder='Exmple: The product is not affected by this vulnerability because ....'
                    value={impact}
                    onChange={(e) => setImpact(e.target.value)}
                    fontSize='sm'
                  />
                </FormControl>
              </Card>
            )}

            {/* RESPONSE */}
            {statusName === 'Affected' && (
              <Card position='relative' p={6} border={border}>
                {generateIcon(detailsCheckType)}
                {/* ACTION STATEMENT */}
                <FormControl>
                  <InfoLabel
                    title={'Action Statement'}
                    name={'actionStatement'}
                  />
                  <Textarea
                    rows={2}
                    name='actionStatement'
                    id='actionStatement'
                    placeholder='Example: This vulnerability can be mitigate by running the application with ENV_PROTECTED enabled or turning off Notifications under settings.'
                    fontSize='sm'
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                  />
                </FormControl>
                {/* RESPONSE */}
                <FormControl mt={5}>
                  <InfoLabel title={'Response'} name={'cdxResponseId'} />
                  {allCdx && (
                    <Select
                      id='cdxResponseId'
                      name='cdxResponseId'
                      value={cdxResponseId}
                      onChange={handleResponseChange}
                      fontSize='sm'
                    >
                      <option value=''>-- Select --</option>
                      {allCdx?.cdxResponses?.length > 0 &&
                        allCdx?.cdxResponses?.map((item, idx) => (
                          <option key={idx} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Select>
                  )}
                </FormControl>
                {/* FIXED VERSION */}
                {responseTitle === 'Update' && (
                  <FormControl
                    mt={5}
                    width={'100%'}
                    isRequired={responseTitle === 'Update'}
                  >
                    <InfoLabel title={'Fixed Version'} name={'fixedIn'} />
                    <Select
                      id='fixedIn'
                      name='fixedIn'
                      value={fixedIn}
                      onChange={handleFixedInChange}
                      fontSize='sm'
                    >
                      <option value=''>-- Select --</option>
                      {fixedVersions?.length > 0 &&
                        fixedVersions?.map((item, index) => (
                          <option key={index} value={item} name={item}>
                            {item}
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                )}
              </Card>
            )}

            {/* DETAILS */}
            {statusName === 'In Triage' && (
              <Card position='relative' p={6} border={border}>
                {generateIcon(detailsCheckType)}
                <FormControl>
                  <InfoLabel title={'Details'} name={'details'} />
                  <Textarea
                    rows={2}
                    name='details'
                    id='details'
                    placeholder='Example: The vulnerability surfaced in the reports on March 13th 3pm and has been sent to PSIRT for analysis by 7pm.'
                    fontSize='sm'
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                  />
                </FormControl>
              </Card>
            )}

            {/* INTERNAL NOTES */}
            <Card position='relative' p={6} border={border}>
              {generateIcon(notesCheckType)}
              <FormControl mt={statusName === 'In Triage' ? 5 : 0}>
                <InfoLabel title={'Internal Notes'} name={'internalNotes'} />
                <Textarea
                  rows={2}
                  name='internalNotes'
                  id='internalNotes'
                  aria-label='vex_notes'
                  placeholder='Example: John Appleseed has scanned the codebase and found two instances of function alls encrypt(). Next step: exploitability analysis.'
                  fontSize='sm'
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  isDisabled={!editVulns}
                />
              </FormControl>
            </Card>

            {/* CUSTOM FIELDS */}
            {nodes?.length > 0 && (
              <Card p={6} border={border} position='relative'>
                {generateIcon(riskCheckType)}
                {/* FIELDS */}
                {fieldOne !== undefined && (
                  <FormControl>
                    <FormLabel>{fieldOne?.displayName}</FormLabel>
                    <Select
                      isDisabled={fieldOne === undefined}
                      fontSize='sm'
                      value={formValues ? formValues[fieldOne?.id] : ''}
                      onChange={(e) =>
                        handleChange(fieldOne?.id, e.target.value)
                      }
                    >
                      <option value={''}>-- Select --</option>
                      {Array.from(
                        { length: fieldOne?.maxValue - fieldOne?.minValue + 1 },
                        (_, i) => (
                          <option key={i} value={fieldOne?.minValue + i}>
                            {fieldOne?.minValue + i}
                          </option>
                        )
                      )}
                    </Select>
                  </FormControl>
                )}
                {fieldTwo !== undefined && (
                  <FormControl mt={5}>
                    <FormLabel>{fieldTwo?.displayName}</FormLabel>
                    <Input
                      isDisabled={fieldTwo === undefined}
                      fontSize={'sm'}
                      value={formValues ? formValues[fieldTwo?.id] : ''}
                      onChange={(e) =>
                        handleChange(fieldTwo?.id, e.target.value)
                      }
                    />
                  </FormControl>
                )}
              </Card>
            )}

            {/* UPSTERAM PRODUCT */}
            <FormControl isDisabled={vexStatusId === ''}>
              <Checkbox
                size='sm'
                isChecked={propagateVex}
                onChange={(e) => setPropagateVex(e.target.checked)}
              >
                Also update upstream products
              </Checkbox>
            </FormControl>

            <ButtonGroup>
              <Button
                title='Save vuln status'
                colorScheme='blue'
                onClick={handleSave}
                isLoading={loading}
                sx={{ w: '100px', fontSize: 'sm' }}
                isDisabled={saveButtonDisable || !editVulns || isInvalid}
              >
                {locallySavedLabel || 'Save'}
              </Button>
              {componentVulnLogs?.length > 0 && (
                <Button
                  title='Show vuln status history'
                  sx={{ fontSize: 'sm' }}
                  onClick={HISTORY.onOpen}
                >
                  Show History
                </Button>
              )}
            </ButtonGroup>
          </SimpleGrid>
        </Box>
      </Stack>

      {HISTORY.isOpen && (
        <HistoryDrawer
          isOpen={HISTORY.isOpen}
          onClose={HISTORY.onClose}
          data={{
            id: data?.id,
            vulnId: data?.vuln?.vulnId,
            logs: componentVulnLogs,
            fields: componentVulnCustomFields || []
          }}
        />
      )}
    </>
  )
}

export default VexStatusComponent
